import {PictureData, ImageSource, Breakpoint, ImageOptions, ImageryOptions} from './types';

/**
 * @method formatImageUrl Returns a formatted image url, with all imagery transformations applied in
 * the form of query string params.

 * @param {string} imageUrl
 * @param {ImageryOptions} options - an object of imagery options (width, height, format)
 * @param {boolean} [retina=false] - boolean to provide 1x or 2x width and height
 */
export function formatImageUrl(imageUrl: string, options: ImageryOptions, retina = false): string {
  const formattedParams = Object.keys(options).map((option) =>
    (typeof options[option] === 'number'
      ? `${option}=${options[option] * (retina ? 2 : 1)}`
      : `${option}=${options[option]}`));

  return `${imageUrl}?${formattedParams.join('&')}`;
}

/**
 * @method makeImageSourceData Returns an object with image srcset, and the formatted media query the srcset applies to
 *
 * @param {string} image1x - the result of formatImageUrl
 * @param {string} image2x - the result of formatImageUrl with retina: true
 * @param {Breakpoint} [breakpoint] - the media query for this given size
 */
export function makeImageSourceData(
  image1x: string,
  image2x: string,
  breakpoint: Breakpoint,
): ImageSource {
  const srcset = `${image1x} 1x, ${image2x} 2x`;

  const minWidth = breakpoint.min === null ? null : `(min-width: ${breakpoint.min}px)`;
  const maxWidth = breakpoint.max === null ? null : `(max-width: ${breakpoint.max}px)`;
  let media;

  if (minWidth === null || maxWidth === null) {
    media = minWidth || maxWidth;
  } else {
    media = `${minWidth} and ${maxWidth}`;
  }

  return {srcset, media};
}

/**
 * @method makePictureData - returns an array of source objects, and a fallback url for the picture element
 *
 * @param {string} imageUrl
 * @param {ImageOptions} [imageOptions] - an array of objects: breakpoint: Breakpoint, fallback: boolean, options: ImageryOptions
 */
export function makePictureData(imageUrl: string, imageOptions: ImageOptions[]): PictureData {
  let fallbackSrc;
  const sources: ImageSource[] = [];

  imageOptions.map((optionObject) => {
    const {breakpoint, options} = optionObject;
    sources.push(
      makeImageSourceData(
        formatImageUrl(imageUrl, options),
        formatImageUrl(imageUrl, options, true),
        breakpoint,
      ),
    );
    if (optionObject.hasOwnProperty('fallback')) {
      fallbackSrc = formatImageUrl(imageUrl, options);
    }
  });

  if (!fallbackSrc) {
    fallbackSrc = formatImageUrl(imageUrl, imageOptions[0].options);
  }

  return {sources, fallback: fallbackSrc};
}
